import type { ReactNode } from "react";
import { Building2, ChevronRight } from "lucide-react";
import { DataTable, type DataTableColumn, type DataTableServerPagination } from "@/components/ui/data-table";
import { UserAvatar } from "@/components/ui/user-avatar";
import { formatPlanDate } from "@/lib/builder/utils/payment-format";
import { useI18n } from "@/hooks/useI18n";
import type { PlatformFeeOwnerKind, PlatformFeeOwnerRow } from "@/integrations/supabase/rpc-types";
import { PlatformFeeRateBadge } from "@/lib/dashboard/agencies/platform-fee-display";

interface AgenciesTableProps {
  kind: PlatformFeeOwnerKind;
  rows: PlatformFeeOwnerRow[];
  selectedId: string | null;
  onSelect: (row: PlatformFeeOwnerRow) => void;
  pageSize: number;
  serverPagination: DataTableServerPagination;
  isLoading: boolean;
  animationKey: number;
  emptyState: ReactNode;
}

export function AgenciesTable({
  kind,
  rows,
  selectedId,
  onSelect,
  pageSize,
  serverPagination,
  isLoading,
  animationKey,
  emptyState,
}: AgenciesTableProps) {
  const { t } = useI18n();

  const columns: DataTableColumn<PlatformFeeOwnerRow>[] = [
    {
      id: "name",
      header: kind === "agency"
        ? t("dashboard.overview.agencies.columnAgency")
        : t("dashboard.overview.agencies.columnAdvisor"),
      cell: (row) => (
        <UserAvatar expanded fullName={row.name} email={row.email} size="sm" />
      ),
    },
    // I membri contano solo per le agenzie: un indipendente lavora da solo.
    ...(kind === "agency"
      ? [
          {
            id: "members",
            header: t("dashboard.overview.agencies.columnMembers"),
            headClassName: "w-px whitespace-nowrap text-right",
            cellClassName: "text-right text-gray-600 tabular-nums",
            cell: (row: PlatformFeeOwnerRow) => row.members_count,
          },
        ]
      : []),
    {
      id: "stripe",
      header: t("dashboard.overview.agencies.columnStripe"),
      headClassName: "w-px whitespace-nowrap",
      cellClassName: "whitespace-nowrap text-sm",
      cell: (row) =>
        row.stripe_charges_enabled ? (
          <span className="text-green-700">{t("dashboard.overview.agencies.stripeActive")}</span>
        ) : (
          <span className="text-gray-500">{t("dashboard.overview.agencies.stripeInactive")}</span>
        ),
    },
    {
      id: "rate",
      header: t("dashboard.overview.agencies.columnRate"),
      headClassName: "w-px whitespace-nowrap",
      cell: (row) => <PlatformFeeRateBadge rate={row.rate} isCustom={row.custom_rate !== null} />,
    },
    {
      id: "updated",
      header: t("dashboard.overview.agencies.columnUpdated"),
      headClassName: "w-px whitespace-nowrap",
      cellClassName: "text-gray-600 whitespace-nowrap text-sm",
      cell: (row) => (row.rate_updated_at ? formatPlanDate(row.rate_updated_at) : "—"),
    },
    {
      id: "open",
      header: null,
      headClassName: "w-8",
      cellClassName: "text-right",
      cell: () => <ChevronRight className="h-4 w-4 text-gray-400" />,
    },
  ];

  return (
    <DataTable
      data={rows}
      columns={columns}
      getRowId={(row) => row.id}
      onRowClick={onSelect}
      selectedRowId={selectedId}
      pageSize={pageSize}
      serverPagination={serverPagination}
      isLoading={isLoading}
      animationKey={animationKey}
      emptyState={emptyState}
      tableClassName="table-auto min-w-[760px]"
    />
  );
}

export function AgenciesEmpty({ filtered }: { filtered: boolean }) {
  const { t } = useI18n();

  return (
    <div className="flex flex-col items-center gap-2 text-center text-gray-500">
      <Building2 className="h-8 w-8 text-gray-300" />
      <p className="text-sm">
        {filtered ? t("dashboard.overview.agencies.emptyFiltered") : t("dashboard.overview.agencies.empty")}
      </p>
    </div>
  );
}

export default AgenciesTable;
